/**
 * ============================================
 * QUOTATIONS API SERVICE - COMPLETE VERSION
 * ============================================
 * 
 * ✅ Full versioning support
 * ✅ PDF download functionality
 * ✅ Version history fetching
 * ✅ Manual product saving
 * ✅ Service images support (NEW!)
 * ✅ Comprehensive logging
 * ✅ Error handling
 * ✅ Multi-response format handling
 */

import axios from 'axios';
import { tokenStorage } from '../utils/tokenStorage';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true'
  }
});

// Add auth token to requests with logging
axiosInstance.interceptors.request.use((config) => {
  const token = tokenStorage.getApiAccessToken();
  
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    console.log('✅ API token found and added to request');
  } else {
    console.warn('⚠️ WARNING: No auth token found in localStorage!');
    console.warn('Token key: voicebot_access_token');
  }
  
  console.log(`📡 API Request: ${config.method.toUpperCase()} ${config.baseURL}${config.url}`);
  
  return config;
});

// Add response logging
axiosInstance.interceptors.response.use(
  (response) => {
    console.log(`✅ Response: ${response.status} from ${response.config.url}`, response.data);
    return response;
  },
  (error) => {
    console.error(`❌ Error: ${error.response?.status || 'Network Error'}`, {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      message: error.response?.data?.message || error.message
    });
    return Promise.reject(error);
  }
);

export const quotationsAPI = {
  /**
   * Get all quotations with pagination and filtering
   */
  getAll: async (params = {}) => {
    try {
      console.log('📥 Fetching quotations with params:', params);
      const response = await axiosInstance.get('/quotations', { params });
      
      console.log('📦 Full API Response:', response.data);
      console.log('Response structure:', {
        hasSuccess: !!response.data?.success,
        hasData: !!response.data?.data,
        dataKeys: Object.keys(response.data?.data || {})
      });
      
      // Handle multiple possible response structures
      let quotations = [];
      
      if (response.data?.data?.quotations) {
        quotations = response.data.data.quotations;
        console.log('✅ Found quotations at: response.data.data.quotations');
      } else if (response.data?.quotations) {
        quotations = response.data.quotations;
        console.log('✅ Found quotations at: response.data.quotations');
      } else if (Array.isArray(response.data?.data)) {
        quotations = response.data.data;
        console.log('✅ Found quotations at: response.data.data');
      } else if (Array.isArray(response.data)) {
        quotations = response.data;
        console.log('✅ Found quotations at: response.data (direct array)');
      }
      
      console.log(`📊 Retrieved ${quotations.length} quotations`);
      
      return {
        ...response.data,
        data: {
          quotations: quotations,
          pagination: response.data?.data?.pagination || response.data?.pagination || {}
        }
      };
    } catch (error) {
      console.error('❌ Failed to fetch quotations:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  /**
   * Get single quotation by ID with items and version history
   */
  getById: async (quotationId) => {
    try {
      console.log(`📥 Fetching quotation: ${quotationId}`);
      const response = await axiosInstance.get(`/quotations/${quotationId}`);
      
      let quotation = response.data?.data?.quotation || response.data?.quotation;
      console.log(`✅ Quotation loaded: ${quotation?.quotation_number}`);
      console.log(`📊 Version: ${quotation?.version}, Is Latest: ${quotation?.is_latest}`);
      
      if (response.data?.data?.quotation?.versionHistory) {
        console.log('📜 Version History:', response.data.data.quotation.versionHistory);
      }
      
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to fetch quotation ${quotationId}:`, error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  /**
   * Get all versions of a quotation
   */
  getVersionHistory: async (quotationId) => {
    try {
      console.log(`📥 Fetching version history for quotation: ${quotationId}`);
      const response = await axiosInstance.get(`/quotations/${quotationId}/versions`);
      
      const versions = response.data?.data?.versions || [];
      console.log(`✅ Found ${versions.length} versions`);
      
      return {
        success: true,
        data: {
          versions: versions
        }
      };
    } catch (error) {
      console.error(`❌ Failed to fetch version history:`, error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  /**
   * Create new quotation
   */
  create: async (quotationData) => {
    try {
      console.log('📝 Creating quotation:', quotationData);
      const response = await axiosInstance.post('/quotations', quotationData);
      
      console.log('✅ Create response received:', response.data);
      
      let quotation = response.data?.data?.quotation;
      console.log(`✅ Quotation created: ${quotation?.quotation_number}`);
      
      return {
        success: response.data?.success || true,
        data: {
          quotation: quotation
        }
      };
    } catch (error) {
      console.error('❌ Failed to create quotation:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  /**
   * Update existing quotation (creates new version)
   */
  update: async (quotationId, quotationData) => {
    try {
      console.log(`📝 Updating quotation ${quotationId}:`, quotationData);
      const response = await axiosInstance.put(`/quotations/${quotationId}`, quotationData);
      
      let quotation = response.data?.data?.quotation;
      console.log(`✅ New version created: ${quotation?.quotation_number}`);
      console.log(`📊 Previous Version: ${response.data?.data?.previousVersion}, New Version: ${response.data?.data?.newVersion}`);
      console.log(`🔗 Old Quotation ID: ${response.data?.data?.oldQuotationId}, New Quotation ID: ${response.data?.data?.newQuotationId}`);
      
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to update quotation ${quotationId}:`, error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  /**
   * Update quotation status
   */
  updateStatus: async (quotationId, status) => {
    try {
      console.log(`📝 Updating quotation ${quotationId} status to: ${status}`);
      const response = await axiosInstance.patch(`/quotations/${quotationId}/status`, { status });
      
      console.log(`✅ Status updated to: ${status}`);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to update status:`, error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  /**
   * Convert quotation to order
   */
  convertToOrder: async (quotationId) => {
    try {
      console.log(`📝 Converting quotation ${quotationId} to order`);
      const response = await axiosInstance.post(`/quotations/${quotationId}/convert-to-order`);
      
      console.log(`✅ Converted to order: ${response.data?.data?.orderNumber}`);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to convert quotation:`, error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  /**
   * Get quotation statistics
   */
  getStats: async (params = {}) => {
    try {
      console.log('📥 Fetching quotation statistics with params:', params);
      const response = await axiosInstance.get('/quotations/stats/overview', { params });
      
      console.log('✅ Stats response:', response.data);
      
      let stats = response.data?.data?.stats || response.data?.stats || {};
      
      console.log('📊 Parsed stats:', stats);
      
      return {
        success: response.data?.success || true,
        data: {
          stats: stats
        }
      };
    } catch (error) {
      console.error('❌ Failed to fetch statistics:', error.response?.data || error.message);
      // Return default stats on error instead of throwing
      return {
        success: false,
        data: {
          stats: {
            totalQuotations: 0,
            draftQuotations: 0,
            sentQuotations: 0,
            acceptedQuotations: 0,
            rejectedQuotations: 0,
            convertedQuotations: 0,
            totalValue: 0,
            acceptedValue: 0,
            convertedValue: 0
          }
        }
      };
    }
  },

  /**
   * ✅ Get products with images for quotation catalog
   * 
   * Fetches products from the catalog with their primary images
   * for display in the quotation form product selector.
   * 
   * @param {string} searchQuery - Optional search term to filter products
   * @returns {Promise<Array>} Array of products with image URLs
   */
  getProductsWithImages: async (searchQuery = '') => {
    try {
      const params = new URLSearchParams();
      if (searchQuery && searchQuery.trim()) {
        params.append('search', searchQuery.trim());
      }
      
      const url = `/quotations/catalog/products${params.toString() ? '?' + params.toString() : ''}`;
      
      console.log(`📥 Fetching products with images${searchQuery ? ` (search: "${searchQuery}")` : ''}`);
      const response = await axiosInstance.get(url);
      
      const products = response.data?.data?.products || [];
      console.log(`✅ Loaded ${products.length} products with images`);
      
      return products;
    } catch (error) {
      console.error('❌ Error fetching products with images:', error);
      return [];
    }
  },

  /**
   * ✅ Get services with images for quotation catalog (NEW!)
   * 
   * Fetches services from the catalog with their primary images
   * for display in the quotation form service selector.
   * Uses the same pattern as products but queries the services table.
   * 
   * @param {string} searchQuery - Optional search term to filter services
   * @returns {Promise<Array>} Array of services with image URLs
   */
  getServicesWithImages: async (searchQuery = '') => {
    try {
      const params = new URLSearchParams();
      if (searchQuery && searchQuery.trim()) {
        params.append('search', searchQuery.trim());
      }
      
      const url = `/quotations/catalog/services${params.toString() ? '?' + params.toString() : ''}`;
      
      console.log(`📥 Fetching services with images${searchQuery ? ` (search: "${searchQuery}")` : ''}`);
      const response = await axiosInstance.get(url);
      
      const services = response.data?.data?.services || [];
      console.log(`✅ Loaded ${services.length} services with images`);
      
      return services;
    } catch (error) {
      console.error('❌ Error fetching services with images:', error);
      return [];
    }
  },

  /**
   * ✅ Save manual product to database
   * 
   * This saves a manually entered product to the products catalog
   * and returns it with a valid UUID for use in quotations.
   * 
   * @param {Object} productData - Product information
   * @param {string} productData.product_name - Product name (required)
   * @param {number} productData.price - Product price (required)
   * @param {string} productData.unit - Unit type (required)
   * @param {number} productData.gst_rate - GST rate percentage (required)
   * @param {string} productData.category - Product category (required)
   * @param {number} [productData.mrp] - MRP (optional, defaults to price)
   * 
   * @returns {Promise<Object>} Response with saved product and valid UUID
   */
  saveManualProduct: async (productData) => {
    try {
      console.log('💾 Saving manual product:', productData);

      // ✅ Validate required fields
      if (!productData.product_name || !productData.product_name.trim()) {
        throw new Error('Product name is required');
      }
      if (!productData.price || parseFloat(productData.price) <= 0) {
        throw new Error('Valid price is required');
      }
      if (!productData.unit || !productData.unit.trim()) {
        throw new Error('Unit is required');
      }
      if (!productData.category || !productData.category.trim()) {
        throw new Error('Category is required');
      }
      if (productData.gst_rate === undefined || productData.gst_rate === null) {
        throw new Error('GST rate is required');
      }

      console.log('✅ Frontend validation passed');

      // ✅ Send price as MRP (not selling_price)
      const response = await axiosInstance.post('/quotations/manual-product/add', {
        product_name: productData.product_name.trim(),
        mrp: parseFloat(productData.price),  // ✅ Send as MRP
        unit: productData.unit.trim(),
        gst_rate: productData.gst_rate !== null && productData.gst_rate !== undefined && productData.gst_rate !== '' ? parseFloat(productData.gst_rate) : null,
        category: productData.category.trim()
      });

      console.log('✅ API response received:', response.data);

      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Failed to save product');
      }

      const product = response.data?.data?.product;

      if (!product || !product.id) {
        throw new Error('Invalid response: Product ID missing');
      }

      console.log(`✅ Product saved to database with UUID: ${product.id}`);
      console.log(`📦 Product data:`, product);

      return response.data;

    } catch (error) {
      const errorMessage = error.message || 'Failed to save product';
      console.error('❌ Error:', errorMessage);
      throw error.response?.data || error;
    }
  },

  /**
   * Download quotation as PDF
   * ✅ PROPER IMPLEMENTATION
   */
  downloadPDF: async (quotationId) => {
    try {
      console.log(`📥 Starting PDF download for quotation: ${quotationId}`);
      
      const token = tokenStorage.getAccessToken();
      
      if (!token) {
        throw new Error('Authentication token not found. Please login again.');
      }

      const authHeader = `Bearer ${token}`;
      console.log('✅ Using auth token for PDF request');

      const pdfUrl = `${API_BASE_URL}/quotations/${quotationId}/pdf`;
      console.log(`📡 Fetching PDF from: ${pdfUrl}`);

      const response = await fetch(pdfUrl, {
        method: 'GET',
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/pdf'
        }
      });

      console.log(`📊 Response status: ${response.status}`);

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ PDF fetch failed: ${response.status}`, errorText);
        
        if (response.status === 401) {
          throw new Error('Unauthorized. Please login again.');
        } else if (response.status === 404) {
          throw new Error('Quotation not found.');
        } else {
          throw new Error(`Failed to download PDF. Status: ${response.status}`);
        }
      }

      const blob = await response.blob();
      console.log(`✅ PDF blob received, size: ${blob.size} bytes`);

      const contentDisposition = response.headers.get('content-disposition');
      let filename = 'quotation.pdf';

      if (contentDisposition && contentDisposition.includes('filename=')) {
        const matches = contentDisposition.match(/filename="(.+?)"/);
        if (matches && matches[1]) {
          filename = matches[1];
          console.log(`📄 Filename from header: ${filename}`);
        }
      }

      const blobUrl = window.URL.createObjectURL(blob);
      console.log(`✅ Blob URL created: ${blobUrl}`);

      const downloadLink = document.createElement('a');
      downloadLink.href = blobUrl;
      downloadLink.download = filename;
      downloadLink.style.display = 'none';

      document.body.appendChild(downloadLink);
      console.log('📌 Download link appended to DOM');

      downloadLink.click();
      console.log('✅ Download triggered');

      setTimeout(() => {
        document.body.removeChild(downloadLink);
        window.URL.revokeObjectURL(blobUrl);
        console.log('🧹 Cleanup completed');
      }, 100);

      return {
        success: true,
        message: `PDF downloaded successfully: ${filename}`,
        filename: filename
      };

    } catch (error) {
      console.error(`❌ PDF download error:`, error);
      
      const errorMessage = error.message || 'Failed to download PDF. Please try again.';
      console.error('Error details:', {
        message: error.message,
        status: error.status,
        statusText: error.statusText
      });

      throw new Error(errorMessage);
    }
  },

  /**
   * Delete quotation (only latest version)
   */
  delete: async (quotationId) => {
    try {
      console.log(`🗑️ Deleting quotation: ${quotationId}`);
      const response = await axiosInstance.delete(`/quotations/${quotationId}`);
      
      console.log(`✅ Quotation deleted`);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to delete quotation:`, error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  /**
   * Send quotation email with Accept/Reject buttons
   */
  sendEmail: async (quotationId) => {
    try {
      console.log(`📧 Sending email for quotation: ${quotationId}`);
      const response = await axiosInstance.post(`/quotations/${quotationId}/send-email`);
      
      console.log(`✅ Email sent successfully`);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to send quotation email:`, error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  /**
   * Send quotation via WhatsApp with dynamic category
   */
  sendWhatsApp: async (quotationId, category = 'quotations') => {
    try {
      console.log(`📱 Sending WhatsApp for quotation: ${quotationId} (Category: ${category})`);
      const response = await axiosInstance.post(`/quotations/${quotationId}/send-whatsapp`, { category });
      
      console.log(`✅ WhatsApp sent successfully`);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to send quotation WhatsApp:`, error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  /**
   * Get quotations by lead ID
   */
  getByLeadId: async (leadId) => {
    try {
      console.log(`📥 Fetching quotations for lead: ${leadId}`);
      const response = await axiosInstance.get(`/quotations/by-lead/${leadId}`);
      
      const quotations = response.data?.data?.quotations || [];
      console.log(`✅ Found ${quotations.length} quotations for lead ${leadId}`);
      
      return {
        success: true,
        data: {
          quotations: quotations,
          count: quotations.length
        }
      };
    } catch (error) {
      console.error(`❌ Failed to fetch quotations for lead ${leadId}:`, error.response?.data || error.message);
      throw error.response?.data || error;
    }
  }
  
};

export default quotationsAPI;